import { currentAppPath, navigateApp, useAppPath } from '@/vite/navigation-store';

/**
 * An app outside a conversation, full screen (docs/adr-plugin-model-uniformization.md, R7): its
 * home (`/apps/<plugin>`: its templates, its example prompts, the user's items, "New …"), a route
 * of its view (`/apps/<plugin>/<route>`, a level the view went to with `page.navigate` or an item
 * of one of its sections), and an item open there (`?item=<id>`). Every level is an address:
 * the chevron of the host's bar, ⌘[ and the browser's back go up one. From a conversation an app
 * only docks in the side panel.
 */
const APP_HOME_PREFIX = '/apps/';

/** The query parameter naming the item the app's page shows. */
const APP_HOME_ITEM_PARAM = 'item';

/** The Library page. */
export const LIBRARY_PATH = '/artifacts';

/** The Library filtered on an app's kinds (`/artifacts?app=`); its header links to the home. */
export const LIBRARY_APP_PARAM = 'app';

/** A level of an app shown full screen. */
export type AppHomeAddress = {
  plugin: string;
  /** The route of its view (`/runs/42`); `null` on its home. */
  route: string | null;
  item: string | null;
};

/** `/runs/42/` and `runs/42` are `/runs/42`; `/` and `''` are no route. */
function normalRoute(route: string | null | undefined): string | null {
  if (route === null || route === undefined) return null;
  const parts = route.split('/').filter((part) => part !== '' && part !== '.' && part !== '..');
  return parts.length === 0 ? null : `/${parts.join('/')}`;
}

/** The address of an app's home, of a route of its view, or of an item there. */
export function appHomePath(plugin: string, item?: string | null, route?: string | null): string {
  const level = normalRoute(route);
  const path = `${APP_HOME_PREFIX}${encodeURIComponent(plugin)}${
    level === null ? '' : level.split('/').map(encodeURIComponent).join('/')
  }`;
  return item ? `${path}?${APP_HOME_ITEM_PARAM}=${encodeURIComponent(item)}` : path;
}

/** Where each address of an app was pushed from: the chevron pops it when it is the level up. */
const pushedFrom = new Map<string, string>();

/**
 * Opens an app full screen (outside any conversation): its home, a route of its view, an item.
 * A new level is a new entry of the history (`replace` changes the level shown instead).
 */
export function openAppHome(
  plugin: string,
  item?: string | null,
  options: { route?: string | null; replace?: boolean } = {},
): void {
  const target = appHomePath(plugin, item, options.route);
  if (options.replace === true) {
    navigateApp(target, true);
    return;
  }
  pushedFrom.set(target, currentAppPath());
  navigateApp(target);
}

/** The app level an address is (`/apps/<plugin>[/<route>][?item=]`), if it is one. */
export function appHomeOf(path: string): AppHomeAddress | null {
  const url = new URL(path, 'https://crowdex.invalid');
  if (!url.pathname.startsWith(APP_HOME_PREFIX)) return null;
  const [first = '', ...rest] = url.pathname.slice(APP_HOME_PREFIX.length).split('/');
  if (first === '') return null;
  let plugin: string;
  let route: string | null;
  try {
    plugin = decodeURIComponent(first);
    route = normalRoute(rest.map(decodeURIComponent).join('/'));
  } catch {
    return null;
  }
  return { plugin, route, item: url.searchParams.get(APP_HOME_ITEM_PARAM) };
}

/**
 * Where a remote app full screen opens (a Crowlingo application's page, `RemoteAppFullScreen`):
 * the address's route with its query and hash, the app's own (`/apps/crowbox/mail?open=7` is
 * `/mail?open=7`, a CrowBox thread). Null on its bare home, or outside an app.
 */
export function appHomeDeepLink(path: string): string | null {
  if (appHomeOf(path) === null) return null;
  const url = new URL(path, 'https://crowdex.invalid');
  const route = url.pathname
    .slice(APP_HOME_PREFIX.length)
    .split('/')
    .slice(1)
    .filter((part) => part !== '' && part !== '.' && part !== '..');
  if (route.length === 0 && url.search === '' && url.hash === '') return null;
  return `/${route.join('/')}${url.search}${url.hash}`;
}

/** The app level on screen now, if one is. */
export function useAppHome(): AppHomeAddress | null {
  return appHomeOf(useAppPath());
}

/**
 * The level above: an item's is the level it was opened on, a route's is the route without its
 * last part, the first route's is the home; the home has none.
 */
export function appParentOf(address: AppHomeAddress): AppHomeAddress | null {
  if (address.item !== null) return { ...address, item: null };
  if (address.route === null) return null;
  const parts = address.route.split('/').filter(Boolean);
  return { ...address, route: parts.length > 1 ? `/${parts.slice(0, -1).join('/')}` : null };
}

/**
 * Goes up a level of the app on screen (the chevron, ⌘[, `page.back`): back in the history when
 * the level up is where this one was pushed from (so the browser's forward comes back), else to
 * the level up in place. False on a home, or outside an app: nothing to go up to.
 */
export function goBackInApp(path: string = currentAppPath()): boolean {
  const address = appHomeOf(path);
  const parent = address === null ? null : appParentOf(address);
  if (address === null || parent === null) return false;
  const target = appHomePath(parent.plugin, parent.item, parent.route);
  if (pushedFrom.get(path) === target && typeof window !== 'undefined') {
    pushedFrom.delete(path);
    window.history.back();
    return true;
  }
  navigateApp(target, true);
  return true;
}

/** The view of an app's own tab (`plugin:<app>/page`), as its tile names it. */
export function appView(appId: string): string {
  return `plugin:${appId}/page`;
}
